import { motion } from "framer-motion";
import { Check } from "lucide-react";

const sections = [
  "Hero avec animation & badges professionnels",
  "Carrousel de logos d'entreprises / clients",
  "Expertise & savoir-faire en visuel",
  "Section \"Pourquoi me choisir\"",
  "Évaluation de compatibilité par besoin",
  "Timeline d'expériences",
  "Étude de cas détaillée",
  "Portfolio de réalisations",
  "Projets personnels",
  "Diplômes & parcours",
  "Veille & centres d'intérêt",
  "Formulaire de contact",
];

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.05, duration: 0.4, ease: "easeOut" as const },
  }),
};

const SellSections = () => {
  return (
    <section className="py-24">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-center gap-16">
          <div className="flex-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
                Modulable
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
                12+ sections{" "}
                <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  sur mesure
                </span>
              </h2>
              <p className="text-muted-foreground text-lg mb-8">
                Chaque section est activable selon votre profil. Votre portfolio est unique.
              </p>
            </motion.div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {sections.map((section, i) => (
                <motion.div
                  key={section}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  className="flex items-center gap-3 p-3 rounded-xl bg-card/60 border border-border/30 hover:border-primary/30 transition-colors"
                >
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3.5 h-3.5 text-primary" />
                  </div>
                  <span className="text-sm font-medium">{section}</span>
                </motion.div>
              ))}
            </div>
          </div>
          <motion.div 
            className="flex-1 relative"
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="rounded-2xl border border-border/50 overflow-hidden shadow-2xl bg-card">
              {/* Browser mockup */}
              <div className="flex items-center gap-2 px-4 py-3 bg-secondary/50 border-b border-border/30">
                <div className="w-3 h-3 rounded-full bg-destructive/50" />
                <div className="w-3 h-3 rounded-full bg-enterprise-warning/50" />
                <div className="w-3 h-3 rounded-full bg-enterprise-success/50" />
                <div className="flex-1 mx-3">
                  <div className="h-5 rounded-md bg-background/60 flex items-center px-3">
                    <span className="text-xs text-primary">🔒 votre-nom.com</span>
                  </div>
                </div>
              </div>
              {/* Portfolio preview */}
              <div className="p-5 space-y-4 bg-gradient-to-br from-background to-secondary/20">
                <div className="flex items-center justify-between">
                  <div className="h-4 w-28 bg-gradient-to-r from-primary to-accent rounded" />
                  <div className="flex gap-3">
                    {[1, 2, 3].map((n) => (
                      <div key={n} className="h-2 w-10 bg-primary/15 rounded" />
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 py-2">
                  <div className="space-y-2.5">
                    <div className="h-5 w-3/4 bg-gradient-to-r from-primary to-accent rounded-lg" />
                    <div className="h-3 w-full bg-primary/15 rounded" />
                    <div className="h-3 w-5/6 bg-primary/10 rounded" />
                    <div className="flex gap-2 pt-1">
                      <div className="h-7 w-20 bg-gradient-to-r from-primary to-accent rounded-lg" />
                      <div className="h-7 w-16 border border-primary/30 rounded-lg" />
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 border border-primary/20" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[1, 2, 3].map((n) => (
                    <div key={n} className="rounded-xl bg-gradient-to-br from-primary/10 to-accent/10 border border-primary/15 p-3 space-y-1.5">
                      <div className="w-6 h-6 rounded-lg bg-primary/25" />
                      <div className="h-1.5 w-full bg-primary/15 rounded" />
                      <div className="h-1.5 w-2/3 bg-primary/10 rounded" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {/* Floating badge */}
            <motion.div 
              className="absolute -bottom-4 -left-4 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-semibold shadow-lg shadow-primary/30"
              animate={{ y: [0, -8, 0] }}
              transition={{ repeat: Infinity, duration: 3 }}
            >
              Clé en main, 0 effort
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default SellSections;
